import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import Lightbox from "react-18-image-lightbox";
import "react-18-image-lightbox/style.css";

/* ==========================================================================
   GAMBAR & VIDEO
   Ganti / tambahkan file foto kamu di folder:
   src/assets/images/coconut-charcoal/1.jpg s/d 6.jpg
   thumbnail video di:
   src/assets/images/coconut-charcoal/video-thumbnail.jpg
   dan file video (mp4) di:
   src/assets/videos/coconut-charcoal-process.mp4
   ========================================================================== */
import Gallery1 from "../assets/images/coconut-charcoal/1.jpeg";
import Gallery2 from "../assets/images/coconut-charcoal/2.jpeg";
import Gallery3 from "../assets/images/coconut-charcoal/3.jpeg";
import Gallery4 from "../assets/images/coconut-charcoal/4.jpeg";
import Gallery5 from "../assets/images/coconut-charcoal/5.jpeg";
import Gallery6 from "../assets/images/coconut-charcoal/6.jpeg";
import VideoThumbnail from "../assets/images/coconut-charcoal/video-thumbnail.jpeg";
import VideoFile from "../assets/videos/coconut-charcoal-process.mp4";

/**
 * Coconut Shell Charcoal (Arang Tempurung Kelapa) Section
 * - Spesifikasi produk
 * - Gallery foto (lightbox)
 * - Video profil produk (file lokal, bukan YouTube)
 */
export default function CoconutCharcoal() {
  const specs = [
    {
      icon: "uil-fire",
      label: "Fixed Carbon",
      value: "75 - 80%",
      desc: "Kandungan karbon padat utama yang membuat arang menyala panas dan tahan lama.",
    },
    {
      icon: "uil-wind",
      label: "Ash Content",
      value: "< 2.5 - 3%",
      desc: "Kadar abu rendah berarti sisa pembakaran minim dan produk lebih bersih.",
    },
    {
      icon: "uil-tear",
      label: "Moisture",
      value: "< 10 - 15%",
      desc: "Kadar air rendah membuat arang mudah dinyalakan dan cepat membara.",
    },
    {
      icon: "uil-cloud",
      label: "Volatile Matter",
      value: "< 15 - 20%",
      desc: "Zat menguap rendah menghasilkan pembakaran tanpa asap hitam berlebih.",
    },
    {
      icon: "uil-temperature",
      label: "Calorific Value",
      value: "6.500 - 7.000 kcal/kg",
      desc: "Nilai kalori tinggi menghasilkan api yang stabil dengan panas maksimal.",
    },
  ];

  const gallery = [
    { image: Gallery1, title: "Raw Coconut Shell" },
    { image: Gallery2, title: "Carbonization Process" },
    { image: Gallery3, title: "Sorting & Sieving" },
    { image: Gallery4, title: "Charcoal Briquette" },
    { image: Gallery5, title: "Quality Inspection" },
    { image: Gallery6, title: "Packing & Loading" },
  ];

  const [photoIndex, setPhotoIndex] = useState(0);
  const [isGalleryOpen, setGalleryOpen] = useState(false);
  const [isPlaying, setPlaying] = useState(false);
  const videoRef = useRef(null);

  const images = gallery.map((item) => item.image);

  const openGallery = (index) => {
    setPhotoIndex(index);
    setGalleryOpen(true);
  };

  const playVideo = () => {
    setPlaying(true);
    if (videoRef.current) {
      videoRef.current.play();
    }
  };

  return (
    <>
      <section
        className="relative md:py-24 py-16 bg-gray-50 dark:bg-slate-800"
        id="coconut-charcoal"
      >
        <div className="container">
          <div className="grid grid-cols-1 pb-8 text-center">
            <h6 className="text-orange-600 text-base font-medium uppercase mb-2">
              New Product
            </h6>
            <h3 className="mb-4 md:text-2xl text-xl font-medium dark:text-white">
              Coconut Shell Charcoal
            </h3>

            <p className="text-slate-400 dark:text-slate-300 max-w-2xl mx-auto">
              Arang tempurung kelapa premium hasil olahan alami, hitam pekat,
              keras, dan tidak mudah hancur — diproses menjadi briket
              menggunakan perekat tepung tapioka tanpa bahan kimia tambahan.
            </p>
          </div>

          {/* Spec cards */}
          <div className="grid grid-cols-1 lg:grid-cols-5 md:grid-cols-2 gap-6 mt-8">
            {specs.map((item, key) => (
              <div
                key={key}
                className="features p-6 text-center shadow-xl shadow-slate-100 dark:shadow-slate-900 hover:shadow-2xl transition duration-500 rounded-3xl bg-white dark:bg-slate-900"
              >
                <div className="w-16 h-16 bg-orange-600/10 text-orange-600 rounded-xl text-2xl flex align-middle justify-center items-center shadow-sm mx-auto">
                  <i className={`uil ${item.icon}`}></i>
                </div>

                <div className="content mt-5">
                  <h5 className="text-md font-semibold dark:text-white">
                    {item.label}
                  </h5>
                  <p className="text-orange-600 text-xl font-bold mt-2 mb-1">
                    {item.value}
                  </p>
                  <p className="text-slate-400 text-sm mt-2">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* ================= GALLERY ================= */}
          <div className="grid grid-cols-1 pb-6 text-center mt-20">
            <h6 className="text-orange-600 text-base font-medium uppercase mb-2">
              Gallery
            </h6>
            <h3 className="mb-4 md:text-2xl text-xl font-medium dark:text-white">
              Proses & Hasil Produk
            </h3>
          </div>

          {/*
            Setiap thumbnail dipaksa rasio 1:1 (aspect-square) + object-cover,
            jadi tetap rapi & seragam walaupun foto asli resolusinya
            berbeda-beda (potret, lanskap, ukuran besar/kecil, dll).
          */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5 mt-8">
            {gallery.map((item, index) => (
              <div
                key={index}
                onClick={() => openGallery(index)}
                className="relative aspect-square rounded-xl overflow-hidden shadow-sm group cursor-pointer bg-slate-200 dark:bg-slate-700"
              >
                <img
                  src={item.image}
                  alt={item.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition duration-500 ease-out"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition duration-500"></div>

                <div className="absolute inset-0 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500">
                  <span className="btn btn-icon rounded-full bg-orange-600 text-white h-10 w-10 flex items-center justify-center mb-2">
                    <i className="uil uil-camera text-base"></i>
                  </span>
                  <span className="text-white text-xs md:text-sm font-medium px-2 text-center">
                    {item.title}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {isGalleryOpen && (
            <Lightbox
              mainSrc={images[photoIndex]}
              nextSrc={images[(photoIndex + 1) % images.length]}
              prevSrc={images[(photoIndex + images.length - 1) % images.length]}
              imageTitle={gallery[photoIndex].title}
              onCloseRequest={() => setGalleryOpen(false)}
              onMovePrevRequest={() =>
                setPhotoIndex((photoIndex + images.length - 1) % images.length)
              }
              onMoveNextRequest={() =>
                setPhotoIndex((photoIndex + 1) % images.length)
              }
            />
          )}

          {/* ================= VIDEO ================= */}
          <div className="grid grid-cols-1 lg:grid-cols-2 md:grid-cols-2 gap-10 items-center mt-20">
            {/* Video kiri */}
            <div className="relative order-1">
              <div className="relative aspect-video rounded-xl overflow-hidden shadow-lg shadow-slate-200 dark:shadow-slate-900 group bg-black">
                <video
                  ref={videoRef}
                  src={VideoFile}
                  poster={VideoThumbnail}
                  controls={isPlaying}
                  playsInline
                  onPause={() => setPlaying(false)}
                  onEnded={() => setPlaying(false)}
                  className="absolute inset-0 w-full h-full object-cover"
                />

                {!isPlaying && (
                  <>
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition duration-500 pointer-events-none"></div>
                    <button
                      type="button"
                      onClick={playVideo}
                      aria-label="Play video"
                      className="absolute inset-0 flex items-center justify-center"
                    >
                      <span className="h-20 w-20 rounded-full shadow-lg shadow-slate-900/50 inline-flex items-center justify-center bg-white/90 group-hover:bg-orange-600 text-orange-600 group-hover:text-white transition-all duration-500">
                        <i className="mdi mdi-play inline-flex items-center justify-center text-3xl"></i>
                      </span>
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Teks kanan */}
            <div className="order-2">
              <h6 className="text-orange-600 text-base font-medium uppercase mb-2">
                Watch Our Process
              </h6>
              <h3 className="mb-4 md:text-2xl text-xl font-medium dark:text-white">
                Dari Tempurung Kelapa Menjadi Arang Berkualitas Ekspor
              </h3>
              <p className="text-slate-400 dark:text-slate-300 mb-6">
                Simak proses karbonisasi, penyortiran, hingga pengemasan arang
                tempurung kelapa kami — diproduksi dengan standar kualitas
                tinggi untuk memenuhi kebutuhan pasar internasional.
              </p>

              <ul className="list-none text-slate-400 dark:text-slate-300 space-y-3">
                <li className="flex items-center">
                  <i className="uil uil-check-circle text-lg text-orange-600 me-2"></i>
                  <span>100% tempurung kelapa alami tanpa campuran</span>
                </li>
                <li className="flex items-center">
                  <i className="uil uil-check-circle text-lg text-orange-600 me-2"></i>
                  <span>Perekat tepung tapioka, tanpa bahan kimia</span>
                </li>
                <li className="flex items-center">
                  <i className="uil uil-check-circle text-lg text-orange-600 me-2"></i>
                  <span>Keras, padat, dan tidak mudah hancur</span>
                </li>
                <li className="flex items-center">
                  <i className="uil uil-check-circle text-lg text-orange-600 me-2"></i>
                  <span>Siap kirim untuk pasar ekspor</span>
                </li>
              </ul>

              <div className="relative mt-8">
                <Link
                  to="#contact"
                  className="btn bg-orange-600 hover:bg-orange-700 border-orange-600 hover:border-orange-700 text-white rounded-md"
                >
                  Request Quotation
                </Link>
              </div>
            </div>
          </div>

          {/* Highlight banner */}
          <div className="grid grid-cols-1 mt-20">
            <div className="rounded-md bg-slate-900 dark:bg-black/40 p-8 md:p-10 text-center">
              <h4 className="text-white text-lg md:text-xl font-medium mb-3">
                Kualitas Premium, Siap Ekspor
              </h4>
              <p className="text-white/60 max-w-2xl mx-auto mb-6">
                Arang berkualitas tinggi kami berwarna hitam pekat, keras, dan
                tidak mudah hancur — diformulasikan khusus untuk kebutuhan
                grilling dan BBQ berstandar internasional.
              </p>
              <Link
                to="#contact"
                className="btn bg-orange-600 hover:bg-orange-700 border-orange-600 hover:border-orange-700 text-white rounded-md"
              >
                Request Quotation
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
